import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  MagnifyingGlassIcon,
  UserPlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline'
import { Card, Badge, Avatar, InlineSpinner, EmptyState, Button, Modal, ModalBody, ModalFooter, Pagination } from '@/components/ui'
import apiClient from '@/api/client'
import type { User, UserRole } from '@/types'

interface AdminUser extends User {
  last_login: string | null
  is_active: boolean
}

export default function AdminUsersPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState(searchParams.get('role') || '')
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '')

  // Create User Modal
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createForm, setCreateForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    role: 'member' as UserRole,
    is_approved: true,
    fiscal_code: '',
    birth_date: '',
    birth_place: '',
  })
  const [createError, setCreateError] = useState<string | null>(null)

  // Edit User Modal
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [editForm, setEditForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    company: '',
    job_title: '',
    bio: '',
    linkedin_url: '',
    fiscal_code: '',
    birth_date: '',
    birth_place: '',
    role: '' as UserRole | '',
    is_approved: false,
    is_active: true,
    password: '',
  })
  const [saving, setSaving] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // Delete User Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Track the actual search term used for API calls (submitted value)
  const [appliedSearch, setAppliedSearch] = useState('')

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true)
      try {
        const response = await apiClient.get('/admin/users/', {
          params: {
            page: currentPage,
            search: appliedSearch || undefined,
            role: roleFilter || undefined,
            status: statusFilter || undefined,
          },
        })
        setUsers(response.data.results)
        setTotalPages(Math.ceil(response.data.count / 20))
        setTotalItems(response.data.count)
      } catch (error) {
        console.error('Error fetching users:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchUsers()
  }, [currentPage, appliedSearch, roleFilter, statusFilter])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/admin/users/', {
        params: {
          page: currentPage,
          search: appliedSearch || undefined,
          role: roleFilter || undefined,
          status: statusFilter || undefined,
        },
      })
      setUsers(response.data.results)
      setTotalPages(Math.ceil(response.data.count / 20))
      setTotalItems(response.data.count)
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    // Update both states - React will batch them and trigger one useEffect
    setCurrentPage(1)
    setAppliedSearch(searchQuery)
  }

  const handleApproveUser = async (userId: number) => {
    try {
      await apiClient.patch(`/admin/users/${userId}/`, { is_approved: true })
      fetchUsers()
    } catch (error) {
      console.error('Error approving user:', error)
    }
  }

  const handleRejectUser = async (userId: number) => {
    try {
      await apiClient.patch(`/admin/users/${userId}/`, { is_approved: false, is_active: false })
      fetchUsers()
    } catch (error) {
      console.error('Error rejecting user:', error)
    }
  }

  const handleEditUser = async (user: AdminUser) => {
    setSelectedUser(user)
    setEditError(null)
    // Carica i dettagli completi dell'utente
    try {
      const response = await apiClient.get(`/admin/users/${user.id}/`)
      const fullUser = response.data
      setEditForm({
        email: fullUser.email || '',
        first_name: fullUser.first_name || '',
        last_name: fullUser.last_name || '',
        phone: fullUser.phone || '',
        company: fullUser.company || '',
        job_title: fullUser.job_title || '',
        bio: fullUser.bio || '',
        linkedin_url: fullUser.linkedin_url || '',
        fiscal_code: fullUser.fiscal_code || '',
        birth_date: fullUser.birth_date || '',
        birth_place: fullUser.birth_place || '',
        role: fullUser.role,
        is_approved: fullUser.is_approved,
        is_active: fullUser.is_active,
        password: '',
      })
    } catch {
      setEditForm({
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: '',
        company: user.company || '',
        job_title: user.job_title || '',
        bio: '',
        linkedin_url: '',
        fiscal_code: user.fiscal_code || '',
        birth_date: user.birth_date || '',
        birth_place: user.birth_place || '',
        role: user.role,
        is_approved: user.is_approved,
        is_active: user.is_active,
        password: '',
      })
    }
    setShowEditModal(true)
  }

  const handleSaveUser = async () => {
    if (!selectedUser) return
    setSaving(true)
    setEditError(null)
    try {
      // Prepara i dati, escludendo password se vuota
      const dataToSend = { ...editForm, birth_date: editForm.birth_date || null }
      if (!dataToSend.password) {
        delete (dataToSend as Record<string, unknown>).password
      }
      await apiClient.patch(`/admin/users/${selectedUser.id}/`, dataToSend)
      setShowEditModal(false)
      setSelectedUser(null)
      fetchUsers()
    } catch (err: unknown) {
      const error = err as { response?: { data?: Record<string, string[]> } }
      if (error.response?.data) {
        const messages = Object.values(error.response.data).flat()
        setEditError(messages.join(', '))
      } else {
        setEditError('Errore durante il salvataggio')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteUser = async () => {
    if (!userToDelete) return
    setDeleting(true)
    try {
      await apiClient.delete(`/admin/users/${userToDelete.id}/`)
      setShowDeleteModal(false)
      setUserToDelete(null)
      fetchUsers()
    } catch (error) {
      console.error('Error deleting user:', error)
    } finally {
      setDeleting(false)
    }
  }

  const handleCreateUser = async () => {
    // Validate with trimmed values
    const email = createForm.email.trim()
    const firstName = createForm.first_name.trim()
    const lastName = createForm.last_name.trim()
    const password = createForm.password

    const errors: string[] = []
    if (!email) errors.push('Email')
    if (!firstName) errors.push('Nome')
    if (!lastName) errors.push('Cognome')
    if (!password) errors.push('Password')
    if (password && password.length < 8) errors.push('Password (min 8 caratteri)')

    if (errors.length > 0) {
      setCreateError(`Campi mancanti o non validi: ${errors.join(', ')}`)
      return
    }

    setSaving(true)
    setCreateError(null)
    try {
      await apiClient.post('/admin/users/', {
        ...createForm,
        email,
        first_name: firstName,
        last_name: lastName,
        birth_date: createForm.birth_date || null,
      })
      setShowCreateModal(false)
      setCreateForm({
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        role: 'member',
        is_approved: true,
        fiscal_code: '',
        birth_date: '',
        birth_place: '',
      })
      fetchUsers()
    } catch (err: unknown) {
      const error = err as { response?: { data?: Record<string, string[]> } }
      if (error.response?.data) {
        const messages = Object.values(error.response.data).flat()
        setCreateError(messages.join(', '))
      } else {
        setCreateError('Errore durante la creazione')
      }
    } finally {
      setSaving(false)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Mai'
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getRoleBadge = (role: UserRole) => {
    const config: Record<string, { variant: 'default' | 'info' | 'warning' | 'success' | 'danger'; label: string }> = {
      super_admin: { variant: 'danger', label: 'Super Admin' },
      admin: { variant: 'danger', label: 'Admin' },
      steering_committee: { variant: 'warning', label: 'Steering Committee' },
      advisory_board: { variant: 'warning', label: 'Advisory Board Tecnico' },
      taskforce_leader: { variant: 'info', label: 'Leader Task Force' },
      trainer: { variant: 'info', label: 'Docente / Formatore' },
      content_reviewer: { variant: 'info', label: 'Revisore contenuti' },
      community_manager: { variant: 'info', label: 'Community Manager' },
      member: { variant: 'default', label: 'Membro' },
      guest: { variant: 'default', label: 'Ospite' },
      // Legacy roles
      scientific_committee: { variant: 'warning', label: 'Comitato Scientifico' },
      expert: { variant: 'info', label: 'Esperto' },
      premium_member: { variant: 'success', label: 'Premium' },
      pending: { variant: 'warning', label: 'In attesa' },
    }
    return config[role] || { variant: 'default', label: role }
  }

  const roleOptions: { value: string; label: string }[] = [
    { value: 'super_admin', label: 'Super Admin' },
    { value: 'admin', label: 'Admin' },
    { value: 'steering_committee', label: 'Steering Committee' },
    { value: 'advisory_board', label: 'Advisory Board Tecnico' },
    { value: 'taskforce_leader', label: 'Leader Task Force' },
    { value: 'trainer', label: 'Docente / Formatore' },
    { value: 'content_reviewer', label: 'Revisore contenuti' },
    { value: 'community_manager', label: 'Community Manager' },
    { value: 'member', label: 'Membro' },
    { value: 'guest', label: 'Ospite' },
  ]

  if (loading && currentPage === 1) {
    return <InlineSpinner message="Caricamento utenti..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Utenti</h1>
          <p className="text-gray-600">{totalItems} utenti registrati</p>
        </div>
        <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => navigate('/gestione/users/import')}>
          <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
          Importa da Excel/CSV
        </Button>
        <Button onClick={() => { setCreateError(null); setShowCreateModal(true); }}>
          <UserPlusIcon className="h-5 w-5 mr-2" />
          Aggiungi Utente
        </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[250px] relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca per nome, email, azienda..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Tutti i ruoli</option>
              {roleOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Tutti gli stati</option>
              <option value="pending">In attesa approvazione</option>
              <option value="active">Attivi</option>
              <option value="inactive">Disattivati</option>
            </select>

            <Button type="submit">
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filtra
            </Button>
          </div>
        </form>
      </Card>

      {/* Users Table */}
      {users.length > 0 ? (
        <>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Utente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ruolo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stato
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registrazione
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ultimo accesso
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => {
                    const roleConfig = getRoleBadge(user.role)

                    return (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <Avatar
                              size="md"
                              name={`${user.first_name} ${user.last_name}`}
                              src={user.avatar || undefined}
                            />
                            <div>
                              <p className="font-medium text-gray-900">
                                {user.first_name} {user.last_name}
                              </p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                              {user.company && (
                                <p className="text-xs text-gray-400">{user.company}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Badge variant={roleConfig.variant}>{roleConfig.label}</Badge>
                        </td>
                        <td className="px-4 py-4">
                          {!user.is_approved ? (
                            <Badge variant="warning">In attesa</Badge>
                          ) : user.is_active ? (
                            <Badge variant="success">Attivo</Badge>
                          ) : (
                            <Badge variant="default">Disattivato</Badge>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {formatDate(user.date_joined)}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {formatDate(user.last_login)}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {!user.is_approved && (
                              <>
                                <button
                                  onClick={() => handleApproveUser(user.id)}
                                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                                  title="Approva"
                                >
                                  <CheckCircleIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleRejectUser(user.id)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                                  title="Rifiuta"
                                >
                                  <XCircleIcon className="h-5 w-5" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleEditUser(user)}
                              className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                              title="Modifica"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => { setUserToDelete(user); setShowDeleteModal(true); }}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                              title="Elimina"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <Card>
          <EmptyState
            icon={<MagnifyingGlassIcon className="h-12 w-12" />}
            title={searchQuery || roleFilter || statusFilter ? 'Nessun utente trovato' : 'Nessun utente'}
            description={
              searchQuery || roleFilter || statusFilter
                ? 'Prova a modificare i filtri di ricerca'
                : 'Non ci sono utenti registrati'
            }
          />
        </Card>
      )}

      {/* Edit User Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Modifica Utente" size="lg">
        <ModalBody>
          {selectedUser && (
            <div className="space-y-4">
              {editError && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {editError}
                </div>
              )}

              <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                <Avatar
                  size="lg"
                  name={`${selectedUser.first_name} ${selectedUser.last_name}`}
                  src={selectedUser.avatar || undefined}
                />
                <div>
                  <p className="font-medium text-gray-900">ID: {selectedUser.id}</p>
                  <p className="text-sm text-gray-500">Registrato: {formatDate(selectedUser.date_joined)}</p>
                </div>
              </div>

              {/* Dati personali */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <input
                    type="text"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cognome</label>
                  <input
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Azienda</label>
                  <input
                    type="text"
                    value={editForm.company}
                    onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo aziendale</label>
                  <input
                    type="text"
                    value={editForm.job_title}
                    onChange={(e) => setEditForm({ ...editForm, job_title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Codice fiscale</label>
                  <input
                    type="text"
                    value={editForm.fiscal_code}
                    onChange={(e) => setEditForm({ ...editForm, fiscal_code: e.target.value.toUpperCase() })}
                    maxLength={20}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono uppercase"
                    placeholder="RSSMRA80A01H501U"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data di nascita</label>
                  <input
                    type="date"
                    value={editForm.birth_date}
                    onChange={(e) => setEditForm({ ...editForm, birth_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Luogo di nascita</label>
                  <input
                    type="text"
                    value={editForm.birth_place}
                    onChange={(e) => setEditForm({ ...editForm, birth_place: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 -mt-2">Dati anagrafici obbligatori per il rilascio degli attestati.</p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
                <input
                  type="url"
                  value={editForm.linkedin_url}
                  onChange={(e) => setEditForm({ ...editForm, linkedin_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="https://linkedin.com/in/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Ruolo e stato */}
              <div className="pt-4 border-t border-gray-100">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo piattaforma</label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value as UserRole })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {roleOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-4 mt-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editForm.is_approved}
                      onChange={(e) => setEditForm({ ...editForm, is_approved: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Approvato</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Attivo</span>
                  </label>
                </div>
              </div>

              {/* Cambio password */}
              <div className="pt-4 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nuova Password <span className="text-gray-400 font-normal">(lascia vuoto per non cambiare)</span>
                </label>
                <input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Minimo 8 caratteri"
                />
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowEditModal(false)}>Annulla</Button>
          <Button onClick={handleSaveUser} disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva modifiche'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete User Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Elimina Utente">
        <ModalBody>
          {userToDelete && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Sei sicuro di voler eliminare l'utente{' '}
                <span className="font-semibold">{userToDelete.first_name} {userToDelete.last_name}</span>?
              </p>
              <p className="text-sm text-red-600">
                Questa azione è irreversibile. Tutti i dati associati all'utente verranno eliminati.
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>Annulla</Button>
          <Button variant="danger" onClick={handleDeleteUser} disabled={deleting}>
            {deleting ? 'Eliminazione...' : 'Elimina utente'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Create User Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Nuovo Utente">
        <ModalBody>
          <div className="space-y-4">
            {createError && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {createError}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={createForm.first_name}
                  onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Mario"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                <input
                  type="text"
                  value={createForm.last_name}
                  onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Rossi"
                />
              </div>
            </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Codice fiscale</label>
                  <input
                    type="text"
                    value={createForm.fiscal_code}
                    onChange={(e) => setCreateForm({ ...createForm, fiscal_code: e.target.value.toUpperCase() })}
                    maxLength={20}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono uppercase"
                    placeholder="RSSMRA80A01H501U"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data di nascita</label>
                  <input
                    type="date"
                    value={createForm.birth_date}
                    onChange={(e) => setCreateForm({ ...createForm, birth_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Luogo di nascita</label>
                  <input
                    type="text"
                    value={createForm.birth_place}
                    onChange={(e) => setCreateForm({ ...createForm, birth_place: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 -mt-2">Dati anagrafici obbligatori per il rilascio degli attestati.</p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="mario.rossi@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
              <input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Minimo 8 caratteri"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo</label>
              <select
                value={createForm.role}
                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as UserRole })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {roleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={createForm.is_approved}
                onChange={(e) => setCreateForm({ ...createForm, is_approved: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Approvato automaticamente</span>
            </label>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Annulla</Button>
          <Button onClick={handleCreateUser} disabled={saving}>
            {saving ? 'Creazione...' : 'Crea utente'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
